'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Globe } from 'lucide-react';
import { useLocale } from 'next-intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { usePathname, useRouter } from '@/i18n/routing';

const locales = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ar', label: 'العربية', short: 'AR' },
];

export default function LanguageDropdown() {
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isArabic = locale === 'ar';
  const toggleDropdown = () => setIsOpen(!isOpen);

  const switchLanguage = useCallback(
    (newLocale: string) => {
      setIsOpen(false);
      router.replace(pathname, {
        locale: newLocale as 'en' | 'ar',
      });
    },
    [pathname, router]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={toggleDropdown}
        className="border-border-soft bg-surface-card text-brand-solid hover:border-brand-primary/30 hover:bg-section-b focus:ring-brand-primary/20 flex size-[41px] items-center justify-center rounded-full border transition-all duration-300 focus:ring-2 focus:outline-none"
        aria-expanded={isOpen}
        aria-label="Select language"
      >
        <Globe className="text-brand-primary size-5" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.ul
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={`border-border-soft bg-surface-card/90 absolute z-[60] mt-2 w-40 overflow-hidden rounded-2xl border p-1.5 shadow-[0px_20px_40px_rgba(0,30,31,0.08)] backdrop-blur-xl dark:shadow-[0px_20px_40px_rgba(0,0,0,0.3)] ${
              isArabic ? 'left-0 origin-top-left' : 'right-0 origin-top-right'
            }`}
          >
            {locales.map((loc) => (
              <li key={loc.code}>
                <button
                  type="button"
                  onClick={() => switchLanguage(loc.code)}
                  className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200 ${
                    locale === loc.code
                      ? 'bg-brand-primary/10 text-brand-primary'
                      : 'text-text-primary hover:bg-section-b'
                  }`}
                >
                  <span className={loc.code === 'ar' ? 'font-arabic' : ''}>
                    {loc.label}
                  </span>
                  {locale === loc.code && (
                    <motion.div
                      layoutId="active-indicator"
                      className="bg-brand-primary size-1.5 rounded-full"
                    />
                  )}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
